import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Gift, Copy, Check } from 'lucide-react';
import { Button } from './ui/button';

const GiftSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [copied, setCopied] = useState(false);

  const IBAN = 'ES4221000053660201037980';

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  const handleCopyIBAN = async () => {
    try {
      await navigator.clipboard.writeText(IBAN);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = IBAN;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <section
      ref={ref}
      className="py-16 md:py-20 px-6 bg-gradient-to-b from-[#F5F0EB] to-[#F9F7F2]"
      data-testid="gift-section">

      <motion.div
        className="max-w-2xl mx-auto text-center"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}>

        {/* Icon */}
        <motion.div
          className="flex justify-center mb-8 md:mb-10"
          variants={itemVariants}>

          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#CCA598]/25 to-[#D7CCC8]/35 shadow-sm">
            <Gift className="w-7 h-7 text-[#8D6E63]" strokeWidth={1.5} />
          </div>
        </motion.div>

        {/* Text */}
        <motion.p
          className="font-body text-base text-[#5D4037] leading-relaxed mb-8 max-w-lg mx-auto"
          variants={itemVariants}>

          Si has recibido esta invitación es porque tu presencia es muy importante para nosotros en este día tan especial. ¡Esperamos que no faltes!
          <br /><br />
          Si estás pensando en hacernos un regalo, puedes hacerlo aquí:
        </motion.p>

        {/* IBAN Container */}
        <motion.div
          className="bg-gradient-to-br from-[#F9F7F2] to-[#F5F0EB] p-6 md:p-8 border border-[#CCA598]/30 shadow-sm"
          variants={itemVariants}>

          <p className="font-body text-xs tracking-[0.15em] uppercase text-[#8D6E63] mb-4">
            IBAN
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <code
              className="font-body text-sm md:text-base text-[#5D4037] tracking-wider bg-white/50 px-4 py-2 border border-[#D7CCC8]"
              data-testid="iban-code">

              {IBAN}
            </code>
            <Button
              onClick={handleCopyIBAN}
              variant="outline"
              className={`font-body text-sm tracking-wider uppercase border-[#CCA598] text-[#5D4037] hover:bg-[#CCA598]/10 transition-all duration-300 px-4 py-2 rounded-none ${copied ? 'copy-success border-[#8F9779] text-[#8F9779] bg-[#8F9779]/10' : ''}`}
              data-testid="copy-iban-btn">

              {copied ?
              <>
                  <Check className="w-4 h-4 mr-2" />
                  Copiado
                </> :

              <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar
                </>
              }
            </Button>
          </div>
        </motion.div>

        {/* Thank you note */}
        <motion.p
          className="font-accent text-xl md:text-2xl text-[#CCA598] mt-8"
          variants={itemVariants}>

          ¡Gracias de corazón!
        </motion.p>
      </motion.div>
    </section>);

};

export default GiftSection;